import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BellSimple, Warning, Robot, PaintBrush, Lightning } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, inr } from "@/lib/api";

export default function AlertsBell({ refreshKey }) {
  const [data, setData] = useState({ threshold: 0, enabled: false, alerts: [] });
  const [agentPending, setAgentPending] = useState([]);
  const [teamPending, setTeamPending] = useState([]);
  const [quotaAlerts, setQuotaAlerts] = useState([]);
  const [thInput, setThInput] = useState("");
  const [enabled, setEnabled] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/alerts").then(({ data }) => setData(data)).catch(() => {});
    api.get("/agent/actions", { params: { status: "pending" } }).then(({ data }) => setAgentPending(data)).catch(() => {});
    api.get("/team/jobs").then(({ data }) =>
      setTeamPending(data.filter((j) => ["awaiting_design_approval", "awaiting_final_approval"].includes(j.stage)))).catch(() => {});
    api.get("/billing/quota-alerts").then(({ data }) => setQuotaAlerts(data.alerts)).catch(() => {});
    api.get("/alerts/settings").then(({ data }) => {
      setThInput(data.cpl_threshold ? String(data.cpl_threshold) : "");
      setEnabled(!!data.enabled);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const save = async () => {
    try {
      await api.put("/alerts/settings", { cpl_threshold: Number(thInput) || 0, enabled });
      toast.success(enabled && Number(thInput) ? `CPL alerts on — threshold ${inr(Number(thInput))}` : "CPL alerts disabled");
      load();
    } catch (e) {
      toast.error("Failed to save alert settings");
    }
  };

  const count = data.alerts.length + agentPending.length + teamPending.length + quotaAlerts.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="alerts-bell-button"
          className="relative h-9 w-9 rounded-full border border-[#E5E7EB] bg-white flex items-center justify-center text-[#525866] hover:text-[#0A0D14] hover:border-[#c9cdd6] transition-colors">
          <BellSimple size={17} weight={count ? "fill" : "regular"} className={count ? "text-red-500" : ""} />
          {count > 0 && (
            <span data-testid="alerts-count-badge" className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{count}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 rounded-lg" data-testid="alerts-popover">
        {agentPending.length > 0 && (
          <button onClick={() => navigate("/automation")} data-testid="bell-agent-section"
            className="w-full px-4 py-3 border-b border-[#E5E7EB] flex items-center gap-3 text-left hover:bg-ai/5 transition-colors">
            <span className="h-8 w-8 shrink-0 rounded-lg bg-ai/10 flex items-center justify-center"><Robot size={16} weight="duotone" className="text-ai" /></span>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold text-[#0A0D14]">{agentPending.length} agent decision{agentPending.length > 1 ? "s" : ""} awaiting your approval</div>
              <div className="text-[11px] text-[#8a90a0] truncate mt-0.5">
                {agentPending.slice(0, 2).map((a) => a.campaign_name || a.params?.adset_name || a.params?.title).filter(Boolean).join(" · ")}
              </div>
            </div>
            <span className="text-[11px] font-bold text-ai shrink-0">Review →</span>
          </button>
        )}
        {teamPending.length > 0 && (
          <button onClick={() => navigate("/team")} data-testid="bell-team-section"
            className="w-full px-4 py-3 border-b border-[#E5E7EB] flex items-center gap-3 text-left hover:bg-ai/5 transition-colors">
            <span className="h-8 w-8 shrink-0 rounded-lg bg-[#0052FF]/10 flex items-center justify-center"><PaintBrush size={16} weight="duotone" className="text-[#0052FF]" /></span>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold text-[#0A0D14]">{teamPending.length} creative{teamPending.length > 1 ? "s" : ""} waiting on you</div>
              <div className="text-[11px] text-[#8a90a0] truncate mt-0.5">
                {teamPending.slice(0, 2).map((j) => `${j.campaign_name} — ${j.stage === "awaiting_design_approval" ? "approve design" : "final sign-off"}`).join(" · ")}
              </div>
            </div>
            <span className="text-[11px] font-bold text-[#0052FF] shrink-0">Review →</span>
          </button>
        )}
        {quotaAlerts.length > 0 && (
          <button onClick={() => navigate("/billing?tab=usage")} data-testid="bell-quota-section"
            className="w-full px-4 py-3 border-b border-[#E5E7EB] flex items-center gap-3 text-left hover:bg-[#FF6B35]/5 transition-colors">
            <span className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center ${quotaAlerts.some((q) => q.severity === "exhausted") ? "bg-red-500/10" : "bg-amber-400/15"}`}>
              <Lightning size={16} weight="duotone" className={quotaAlerts.some((q) => q.severity === "exhausted") ? "text-red-500" : "text-amber-600"} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold text-[#0A0D14]">
                {quotaAlerts.length} AI quota{quotaAlerts.length > 1 ? "s" : ""} {quotaAlerts.some((q) => q.severity === "exhausted") ? "used up or running low" : "running low"}
              </div>
              <div className="text-[11px] text-[#8a90a0] truncate mt-0.5">
                {quotaAlerts.slice(0, 3).map((q) => `${q.name} (${q.remaining} left)`).join(" · ")}
              </div>
            </div>
            <span className="text-[11px] font-bold text-[#FF6B35] shrink-0">Top up →</span>
          </button>
        )}
        <div className="px-4 py-3 border-b border-[#E5E7EB]">
          <div className="font-heading text-sm font-extrabold tracking-tight text-[#0A0D14]">CPL Alerts</div>
          <p className="text-[11px] text-[#8a90a0] mt-0.5">Flags active campaigns whose cost per lead crosses your limit</p>
        </div>
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center gap-3">
          <div className="flex-1">
            <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Max CPL (₹)</label>
            <Input data-testid="alert-threshold-input" value={thInput} onChange={(e) => setThInput(e.target.value.replace(/[^\d.]/g, ""))}
              placeholder="e.g. 250" className="h-9 rounded-lg mt-1 font-mono text-sm" />
          </div>
          <div className="flex flex-col items-center gap-1 pt-4">
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="alert-enabled-switch" />
          </div>
          <Button onClick={save} data-testid="alert-save-button"
            className="mt-4 h-9 rounded-full bg-[#0A0D14] hover:bg-[#1c2230] text-white font-bold text-xs px-4">Save</Button>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {!data.enabled ? (
            <p className="px-4 py-6 text-xs text-[#8a90a0] text-center">Set a max CPL and enable alerts to start monitoring.</p>
          ) : count === 0 ? (
            <p className="px-4 py-6 text-xs text-[#00953e] text-center font-semibold">All active campaigns are under {inr(data.threshold)} CPL ✓</p>
          ) : (
            data.alerts.map((a) => (
              <div key={a.campaign_id} data-testid={`alert-item-${a.campaign_id}`} className="px-4 py-3 border-b border-[#E5E7EB]/50 flex items-start gap-3">
                <Warning size={16} weight="fill" className="text-red-500 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold text-[#0A0D14] truncate">{a.name}</div>
                  <div className="text-[11px] text-[#525866] mt-0.5">
                    CPL <span className="font-mono font-bold text-red-500">{inr(a.cpl)}</span> — {a.over_pct}% over your {inr(data.threshold)} limit · {a.leads} leads
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
